// src/components/approval/ApprovalLine_view.jsx
import React, { useMemo, useState } from "react";

/**
 * Props
 * - lines: 서버가 내려준 approval_lines 배열 (Docs_view에서 전달)
 * - doc:   문서 상세 객체 (필수 아님, 표시용)
 */
const ApprovalLine_view = ({ lines: linesProp = [], doc }) => {

  const [showApprovalLines, setShowApprovalLines] = useState(true);

  // 다양한 키 이름에 대비해 안전하게 필드 정규화
  const lines = useMemo(() => {
    if (!Array.isArray(linesProp)) return [];
    return linesProp.map((l, idx) => ({
      id: l.id ?? idx,
      order: Number(l.order ?? idx + 1),
      approverName:
        l.approver_name ?? l.username ?? l.approver ?? l.name ?? "결재자 미상",
      approverPosition: l.approver_position ?? l.position ?? "",
      category:
        l.approval_category ??
        l.category ??
        (l.status === "reference" ? "reference" : "approval"),
      // is_approved: submit | approved | rejected | wait 등
      state:
        l.is_approved ??
        l.state ??
        (l.status && ["approved", "rejected"].includes(l.status)
          ? l.status
          : "wait"),
      approvedAt: l.approved_at ?? l.approvedAt ?? null,
      signFile: l.sign_file ?? l.signFile ?? null,
    }));
  }, [linesProp]);

  const renderStatus = (line) => {
    // 참조 라인은 고정 표시
    if (line.category === "reference") {
      return (
        <div className="text-center">
          <div className="text-indigo-500 text-lg">ⓘ</div>
          <div className="text-[11px] text-indigo-500">참조</div>
        </div>
      );
    }

    switch (line.state) {
      case "approved":
        return line.signFile ? (
          <img
            src={line.signFile}
            alt="서명"
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="text-center">
            <div className="text-green-600 text-lg">✓</div>
            <div className="text-xs text-green-600">승인</div>
          </div>
        );
      case "rejected":
        return (
          <div className="text-center">
            <div className="text-red-600 text-lg">✗</div>
            <div className="text-xs text-red-600">반려</div>
          </div>
        );
      case "submit":
      case "pending":
      case "wait":
      case "":
      case null:
      default:
        return (
          <div className="text-center">
            <div className="text-gray-400 text-lg">○</div>
            <div className="text-xs text-gray-400">미결</div>
          </div>
        );
    }
  };

  // 참조 라인만 분리 표기 (선택)
  const references = useMemo(
    () => lines.filter((l) => l.category === "reference"),
    [lines]
  );

  // 직급 우선순위 (낮을수록 먼저 표시)
  const positionRank = {
    '사원': 1, '주임': 2, '대리': 3, '과장': 4, '차장': 5,
    '부장': 6, '이사': 7, '상무': 8, '전무': 9, '부사장': 10, '사장': 11
  };

  const getPositionRank = (position) => {
    if (!position) return 999;
    for (const [key, rank] of Object.entries(positionRank)) {
      if (position.includes(key)) return rank;
    }
    return 999;
  };

  // 결재 라인 직급순 정렬
  const approvals = useMemo(
    () => lines
      .filter((l) => l.category !== "reference")
      .sort((a, b) => {
        const rankA = getPositionRank(a.approverPosition);
        const rankB = getPositionRank(b.approverPosition);
        if (rankA === rankB) {
          return (a.approverName || '').localeCompare(b.approverName || '', 'ko');
        }
        return rankA - rankB;
      }),
    [lines]
  );

  return (
    <div className="space-y-3">
      {/* 결재선 토글 */}
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">결재선</span>
        <button
          onClick={() => setShowApprovalLines((v) => !v)}
          className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400"
        >
          {showApprovalLines ? "숨기기" : "보기"}
        </button>
      </div>

      {/* 결재선 */}
      {showApprovalLines && (
        <div className="flex flex-wrap gap-3 mt-1">
          {approvals.length === 0 ? (
            <div className="text-xs text-gray-500">결재 이력이 없습니다.</div>
          ) : (
            approvals.map((line, idx) => (
              <div
                key={line.id}
                className="flex flex-col items-center space-y-2 p-2 border border-gray-200 rounded-md min-w-[88px] bg-white shadow-sm"
              >
                {/* 순번 */}
                <div className="w-6 h-6 bg-gray-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                  {idx + 1}
                </div>

                {/* 결재자 */}
                <div className="text-center">
                  <div className="font-medium text-xs text-gray-800">
                    {line.approverName}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {line.approverPosition || "역할 없음"}
                  </div>
                </div>

                {/* 상태 */}
                <div className="w-12 h-12 border border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                  {renderStatus(line)}
                </div>

                {/* 승인일 */}
                {line.state === "approved" && line.approvedAt && (
                  <div className="text-[11px] text-gray-500">
                    {new Date(line.approvedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* 참조인 */}
      {references.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            참조인
          </label>
          <div className="flex flex-wrap gap-2">
            {references.map((r) => (
              <span
                key={`ref-${r.id}`}
                className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
              >
                {r.approverName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalLine_view;
